import React, { useEffect, useState, useRef, createElement } from 'react';
import { UploadIcon, XIcon, FileIcon, CheckIcon, AlertCircleIcon, PlayIcon, PauseIcon, ShieldIcon, SmartphoneIcon, InfoIcon } from 'lucide-react';
import { uploadFile, generateStoragePath } from '../firebase/storage';
interface VideoMetadata {
  width: number;
  height: number;
  isVertical: boolean;
  duration: number;
  aspectRatio: number;
}
interface VideoUploadProps {
  userId: string;
  onUploadComplete: (downloadUrl: string, metadata?: VideoMetadata) => void;
  onUploadError?: (error: Error) => void;
  onUploadStart?: () => void;
  accept?: string;
  maxSizeMB?: number;
  className?: string;
  buttonText?: string;
  showPreview?: boolean;
  previewUrl?: string;
  isUploading?: boolean;
  maxDurationSeconds?: number;
  enforceVerticalFormat?: boolean;
}
export const VideoUpload: React.FC<VideoUploadProps> = ({
  userId,
  onUploadComplete,
  onUploadError,
  onUploadStart,
  accept = 'video/*',
  maxSizeMB = 100,
  className = '',
  buttonText = 'Upload Video',
  showPreview = true,
  previewUrl,
  isUploading: externalIsUploading,
  maxDurationSeconds = 60,
  enforceVerticalFormat = false
}) => {
  const [file, setFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [preview, setPreview] = useState<string | null>(previewUrl || null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [duration, setDuration] = useState<number | null>(null);
  const [uploadStartTime, setUploadStartTime] = useState<number | null>(null);
  const [uploadTimeoutDetected, setUploadTimeoutDetected] = useState(false);
  const [videoMetadata, setVideoMetadata] = useState<VideoMetadata | null>(null);
  const [dimensionsError, setDimensionsError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const uploadTaskRef = useRef<{
    cancel: () => void;
  } | null>(null);
  const uploadTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  // Use external uploading state if provided
  const uploading = externalIsUploading !== undefined ? externalIsUploading : isUploading;
  // Timeout detection for stalled uploads
  useEffect(() => {
    if (isUploading && uploadProgress === 0) {
      // If upload has started but progress is still 0% after 10 seconds, it's likely stalled
      const timeoutId = setTimeout(() => {
        if (uploadProgress === 0) {
          setUploadTimeoutDetected(true);
          setError('Upload appears to be stalled. This might be due to insufficient permissions or network issues. Please check your connection and try again.');
        }
      }, 10000); // 10 seconds timeout
      uploadTimeoutRef.current = timeoutId;
      return () => {
        if (uploadTimeoutRef.current) {
          clearTimeout(uploadTimeoutRef.current);
        }
      };
    }
  }, [isUploading, uploadProgress]);
  // Function to validate video dimensions
  const validateVideoDimensions = (width: number, height: number): boolean => {
    if (!enforceVerticalFormat) return true;
    // For vertical format, we require height > width (portrait orientation)
    const isVertical = height > width;
    if (!isVertical) {
      setDimensionsError(`Video must be in vertical (portrait) format. Your video is ${width}×${height} (landscape). Recommended: 1080×1920 for best quality.`);
      return false;
    }
    setDimensionsError(null);
    return true;
  };
  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const selectedFile = e.target.files[0];
      // Reset previous errors and timeout state
      setError(null);
      setDimensionsError(null);
      setUploadTimeoutDetected(false);
      // Check if file is a video
      if (!selectedFile.type.startsWith('video/')) {
        setError('Please upload a video file');
        return;
      }
      // Check file size
      if (selectedFile.size > maxSizeMB * 1024 * 1024) {
        setError(`File size exceeds ${maxSizeMB}MB limit`);
        return;
      }
      setFile(selectedFile);
      // Create preview for video
      if (showPreview) {
        const videoUrl = URL.createObjectURL(selectedFile);
        setPreview(videoUrl);
        // Check video duration and dimensions
        const video = document.createElement('video');
        video.preload = 'metadata';
        video.onloadedmetadata = () => {
          URL.revokeObjectURL(video.src);
          // Check duration
          if (video.duration > maxDurationSeconds) {
            setError(`Video duration exceeds ${maxDurationSeconds} seconds limit`);
            setPreview(null);
            setFile(null);
            return;
          }
          // Capture video metadata
          const width = video.videoWidth;
          const height = video.videoHeight;
          const isVertical = height > width;
          const videoDuration = video.duration;
          const aspectRatio = width / height;
          // Check dimensions for vertical format
          const dimensionsValid = validateVideoDimensions(width, height);
          if (!dimensionsValid) {
            setFile(null);
            return;
          }
          setDuration(videoDuration);
          setVideoMetadata({
            width,
            height,
            isVertical,
            duration: videoDuration,
            aspectRatio
          });
          // Only auto-upload if validation passes
          if (!error && !dimensionsError) {
            handleUpload(selectedFile);
          }
        };
        video.onerror = () => {
          setError('Failed to load video. The file might be corrupted.');
          setPreview(null);
          setFile(null);
        };
        video.src = videoUrl;
      } else {
        // Auto-upload the file if it passes validation
        if (!error) {
          handleUpload(selectedFile);
        }
      }
    }
  };
  const handleUpload = async (selectedFile: File = file!) => {
    if (!selectedFile || !userId) return;
    // If we have a dimensions error, don't proceed with upload
    if (dimensionsError) {
      console.error('Cannot upload: dimension requirements not met', dimensionsError);
      return;
    }
    try {
      // Reset state
      setIsUploading(true);
      setUploadProgress(0);
      setUploadStartTime(Date.now());
      setUploadTimeoutDetected(false);
      setError(null);
      if (onUploadStart) onUploadStart();
      // Generate path with appropriate extension
      const path = generateStoragePath(userId, 'video', selectedFile.name);
      // Upload to Firebase
      uploadTaskRef.current = uploadFile(selectedFile, path, progress => {
        setUploadProgress(progress);
        // Clear timeout on first progress update
        if (progress > 0 && uploadTimeoutRef.current) {
          clearTimeout(uploadTimeoutRef.current);
          uploadTimeoutRef.current = null;
        }
      }, error => {
        console.error('Upload error:', error);
        // Provide more helpful error messages based on error code
        if (error.code === 'storage/unauthorized') {
          setError('Permission denied. You do not have access to upload to this location. Please check your Firebase Storage rules.');
        } else if (error.code === 'storage/canceled') {
          setError('Upload was canceled');
        } else if (error.code === 'storage/quota-exceeded') {
          setError('Storage quota exceeded. Please contact support.');
        } else if (error.code === 'storage/invalid-argument') {
          setError('Invalid file or storage path. Please try again with a different file.');
        } else if (error.code === 'storage/unknown') {
          setError('Network error. Please check your connection and try again.');
        } else {
          setError(error.message);
        }
        setIsUploading(false);
        if (onUploadError) onUploadError(error);
      }, downloadUrl => {
        setIsUploading(false);
        setUploadProgress(100);
        // Pass video metadata along with the download URL
        if (videoMetadata) {
          onUploadComplete(downloadUrl, videoMetadata);
        } else {
          onUploadComplete(downloadUrl);
        }
      },
      // Add validation callback
      isValid => {
        if (!isValid) {
          setError('Failed to initialize upload. Please check your permissions and network connection.');
          setIsUploading(false);
          if (onUploadError) onUploadError(new Error('Upload initialization failed'));
        }
      });
      // If uploadTaskRef is null, the upload didn't start properly
      if (!uploadTaskRef.current) {
        throw new Error('Failed to start upload');
      }
    } catch (err) {
      console.error('Upload initialization error:', err);
      setError('Upload failed: ' + (err instanceof Error ? err.message : 'Unknown error'));
      setIsUploading(false);
      if (onUploadError) onUploadError(err as Error);
    }
  };
  const cancelUpload = () => {
    if (uploadTaskRef.current) {
      uploadTaskRef.current.cancel();
      uploadTaskRef.current = null;
    }
    // Clear any timeout
    if (uploadTimeoutRef.current) {
      clearTimeout(uploadTimeoutRef.current);
      uploadTimeoutRef.current = null;
    }
    setIsUploading(false);
    setUploadProgress(0);
    setUploadTimeoutDetected(false);
  };
  const triggerFileInput = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };
  const resetUpload = () => {
    setFile(null);
    setPreview(null);
    setError(null);
    setDimensionsError(null);
    setUploadProgress(0);
    setUploadTimeoutDetected(false);
    // Clear any timeout
    if (uploadTimeoutRef.current) {
      clearTimeout(uploadTimeoutRef.current);
      uploadTimeoutRef.current = null;
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };
  const togglePlayPause = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };
  // Format file size for display
  const formatFileSize = (sizeInBytes: number): string => {
    if (sizeInBytes < 1024) {
      return `${sizeInBytes} B`;
    } else if (sizeInBytes < 1024 * 1024) {
      return `${(sizeInBytes / 1024).toFixed(1)} KB`;
    } else {
      return `${(sizeInBytes / (1024 * 1024)).toFixed(2)} MB`;
    }
  };
  // Format duration for display
  const formatDuration = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.floor(seconds % 60);
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };
  return <div className={`w-full ${className}`} data-id="element-2983">
      <input type="file" ref={fileInputRef} onChange={handleFileChange} className="hidden" accept={accept} data-id="element-2984" />
      {error && <div className="mb-2 p-2 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 text-sm rounded-md flex items-center" data-id="element-2985">
          <AlertCircleIcon className="h-4 w-4 mr-1 flex-shrink-0" data-id="element-2986" />
          {error}
          {/* Show additional help for timeout errors */}
          {uploadTimeoutDetected && <div className="mt-2 text-xs" data-id="element-2987">
              <p className="font-semibold" data-id="element-2988">Possible solutions:</p>
              <ul className="list-disc list-inside mt-1" data-id="element-2989">
                <li data-id="element-2990">Check your Firebase Storage security rules</li>
                <li data-id="element-2991">Verify your internet connection</li>
                <li data-id="element-2992">Try uploading a smaller file</li>
                <li data-id="element-2993">Contact your administrator if the problem persists</li>
              </ul>
            </div>}
        </div>}
      {dimensionsError && <div className="mb-2 p-2 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 text-sm rounded-md flex items-start" data-id="element-2994">
          <AlertCircleIcon className="h-4 w-4 mr-1 flex-shrink-0 mt-1" data-id="element-2995" />
          <div data-id="element-2996">
            <p data-id="element-2997">{dimensionsError}</p>
            <p className="mt-1 text-xs" data-id="element-2998">
              Recommended: 1080×1920 for best quality. Any vertical (portrait)
              video is accepted.
            </p>
          </div>
        </div>}
      {!file && !preview ? <div onClick={triggerFileInput} className="border-2 border-dashed border-gray-300 dark:border-[#1A3330] rounded-lg p-12 text-center cursor-pointer hover:border-[#2DB489] dark:hover:border-[#4ED1A0] transition-colors" data-id="element-2999">
          <div className="flex flex-col items-center" data-id="element-3000">
            <UploadIcon className="h-12 w-12 text-gray-400 dark:text-[#7A8F8A] mb-4" data-id="element-3001" />
            <p className="text-base text-gray-600 dark:text-[#B0C1BD] mb-3" data-id="element-3002">
              {buttonText}
            </p>
            <p className="text-sm text-gray-400 dark:text-[#7A8F8A] mt-1" data-id="element-3003">
              Max size: {maxSizeMB}MB
            </p>
            <p className="text-sm text-gray-400 dark:text-[#7A8F8A] mt-1" data-id="element-3004">
              Max duration: {maxDurationSeconds} seconds
            </p>
            <p className="text-sm text-gray-400 dark:text-[#7A8F8A] mt-1" data-id="element-3005">
              Supported formats: MP4, MOV, WebM
            </p>
          </div>
        </div> : <div className="border border-gray-200 dark:border-[#1A3330] rounded-lg overflow-hidden" data-id="element-3006">
          {/* Video preview */}
          {preview ? <div className="relative bg-gray-900 flex justify-center w-full overflow-hidden" data-id="element-3007">
              <video ref={videoRef} src={preview} className={`max-h-[400px] max-w-full w-full object-contain ${videoMetadata?.isVertical ? 'vertical-video' : 'horizontal-video'}`} onEnded={() => setIsPlaying(false)} controls={false} onLoadedMetadata={e => {
          const video = e.currentTarget;
          setDuration(video.duration);
          // Add class based on video orientation
          if (video.videoWidth && video.videoHeight) {
            const isVertical = video.videoHeight > video.videoWidth;
            video.classList.add(isVertical ? 'vertical-video' : 'horizontal-video');
          }
        }} data-id="element-3008" />
              {/* Play/Pause button overlay */}
              {!uploading && <div className="absolute inset-0 flex items-center justify-center" data-id="element-3009">
                  <button onClick={togglePlayPause} className="bg-black/50 text-white p-3 rounded-full hover:bg-black/70 transition-colors" data-id="element-3010">
                    {isPlaying ? <PauseIcon className="h-6 w-6" data-id="element-3011" /> : <PlayIcon className="h-6 w-6" data-id="element-3012" />}
                  </button>
                </div>}
              {/* Remove button */}
              {!uploading && <button onClick={resetUpload} className="absolute top-2 right-2 bg-black/50 text-white p-1 rounded-full hover:bg-black/70 transition-colors" data-id="element-3013">
                  <XIcon className="h-4 w-4" data-id="element-3014" />
                </button>}
              {/* Duration badge */}
              {duration !== null && <div className="absolute bottom-2 right-2 bg-black/70 text-white text-xs px-2 py-1 rounded" data-id="element-3015">
                  {formatDuration(duration)}
                </div>}
              {/* Dimensions badge */}
              {videoMetadata && <div className="absolute bottom-2 left-2 bg-black/70 text-white text-xs px-2 py-1 rounded" data-id="element-3016">
                  {videoMetadata.width}×{videoMetadata.height}
                </div>}
            </div> : <div className="p-4 flex items-center" data-id="element-3017">
              <FileIcon className="h-8 w-8 text-gray-400 dark:text-[#7A8F8A] mr-3" data-id="element-3018" />
              <div className="flex-1 min-w-0" data-id="element-3019">
                <p className="text-sm font-medium text-gray-700 dark:text-[#B0C1BD] truncate" data-id="element-3020">
                  {file?.name || 'Video file'}
                </p>
                {file && <p className="text-xs text-gray-500 dark:text-[#7A8F8A]" data-id="element-3021">
                    {formatFileSize(file.size)}
                  </p>}
              </div>
              {!uploading && <button onClick={resetUpload} className="ml-2 text-gray-400 dark:text-[#7A8F8A] hover:text-gray-600 dark:hover:text-[#B0C1BD]" data-id="element-3022">
                  <XIcon className="h-5 w-5" data-id="element-3023" />
                </button>}
            </div>}
          {/* Progress bar */}
          {uploading && <div className="p-3 bg-gray-50 dark:bg-[#0F1F1D] border-t border-gray-200 dark:border-[#1A3330]" data-id="element-3024">
              <div className="flex items-center justify-between mb-1" data-id="element-3025">
                <span className="text-xs font-medium text-gray-600 dark:text-[#B0C1BD]" data-id="element-3026">
                  {uploadProgress === 0 ? 'Initializing upload...' : uploadProgress < 100 ? 'Uploading...' : 'Processing...'}
                </span>
                <span className="text-xs text-gray-500 dark:text-[#7A8F8A]" data-id="element-3027">
                  {uploadProgress}%
                </span>
              </div>
              <div className="w-full bg-gray-200 dark:bg-[#1A3330] rounded-full h-2 overflow-hidden" data-id="element-3028">
                <div className="bg-[#2DB489] dark:bg-[#4ED1A0] h-2 rounded-full transition-all duration-300 ease-out" style={{
            width: `${uploadProgress}%`
          }} data-id="element-3029"></div>
              </div>
              {/* Show help text if upload is stuck at 0% for a while */}
              {uploadProgress === 0 && uploadStartTime && Date.now() - uploadStartTime > 5000 && <div className="mt-2 flex items-start text-xs text-amber-600 dark:text-amber-400" data-id="element-3030">
                    <ShieldIcon className="h-3.5 w-3.5 mr-1 flex-shrink-0 mt-0.5" data-id="element-3031" />
                    <span data-id="element-3032">
                      Upload seems slow to start. This may be due to permission
                      issues or network connectivity.
                    </span>
                  </div>}
              <div className="mt-2 flex justify-end" data-id="element-3033">
                <button onClick={cancelUpload} className="text-xs text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300" data-id="element-3034">
                  Cancel
                </button>
              </div>
            </div>}
          {/* Success indicator */}
          {!uploading && uploadProgress === 100 && <div className="p-3 bg-green-50 dark:bg-green-900/20 border-t border-green-100 dark:border-green-900/30 flex items-center" data-id="element-3035">
              <CheckIcon className="h-4 w-4 text-green-600 dark:text-green-400 mr-2" data-id="element-3036" />
              <span className="text-sm text-green-700 dark:text-green-400" data-id="element-3037">
                Upload complete
              </span>
            </div>}
        </div>}
    </div>;
};